import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PageShell from "@/components/PageShell";
import PublicReviews from "@/components/PublicReviews";
import { ArrowLeft, ShieldCheck, CalendarHeart, BookOpenCheck } from "lucide-react";

const HERO_BG =
  "https://images.pexels.com/photos/7303842/pexels-photo-7303842.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SERVICES_IMG =
  "https://customer-assets.emergentagent.com/job_counsel-marketplace/artifacts/1mjvbse1_lucid-origin_A_sophisticated_minimalist_abstract_art_sculpture_representing_innovative_consul-0.jpg";
const HERO_BADGE_LOGO =
  "https://customer-assets.emergentagent.com/job_counsel-marketplace/artifacts/fh1z7klb_awtadk%20logo.png";
const BOOK_COVER =
  "https://customer-assets.emergentagent.com/job_counsel-marketplace/artifacts/jkrfq5vv_gemini-2.5-flash-image_Create_an_ultra-realistic_high-end_product_photography_image_for_an_Arabic_digit-0.jpg";

const ValueCard = ({ icon: Icon, title, body }) => (
  <div className="card-warm p-7 fade-up">
    <div className="w-11 h-11 rounded-full bg-[#F6F4EE] grid place-items-center mb-5">
      <Icon className="w-5 h-5 text-[#0A4D3C]" />
    </div>
    <h3 className="font-heading text-2xl text-[#222222] mb-2">{title}</h3>
    <p className="text-[#5E5C58] leading-relaxed">{body}</p>
  </div>
);

const Home = () => {
  return (
    <PageShell>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(253,252,248,0.55) 0%, rgba(253,252,248,0.95) 100%), url('${HERO_BG}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="max-w-2xl fade-up">
            <span
              data-testid="hero-badge"
              className="block leading-none mb-0 pb-0"
              aria-label="أوتادك"
            >
              <img
                src={HERO_BADGE_LOGO}
                alt="أوتادك"
                className="h-24 sm:h-28 lg:h-32 w-auto object-contain select-none block"
                draggable="false"
              />
            </span>
            <h1
              data-testid="hero-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl gold-shimmer mt-1 leading-tight text-balance"
            >
              الحياة تحتاج ثباتًا، لا مقاومة
            </h1>
            <div
              data-testid="hero-consultant"
              className="mt-5 inline-flex items-center gap-3 text-[#5E5C58]"
            >
              <span className="h-px w-10 bg-[#C5A059]" />
              <span className="text-sm sm:text-base">
                <span className="font-medium text-[#0A4D3C] font-heading text-lg">
                  أنا الخالدي
                </span>
              </span>
            </div>
            <p
              data-testid="hero-tagline"
              className="mt-6 font-heading text-2xl sm:text-3xl lg:text-xl xl:text-2xl text-[#0A4D3C] leading-snug tracking-wide lg:whitespace-nowrap"
            >
              دليلك لترميم ذاتك، واستعادة ضبط حياتك لتحقيق سعادتك واستقرارك
            </p>
            <p className="mt-6 text-lg text-[#5E5C58] leading-loose max-w-xl">
              احجز جلسة
              <span className="font-medium text-[#222222]"> «أوتادك» </span>
              أو احصل على نسختك الرقمية من كتاب
              <span className="font-medium text-[#222222]"> «ثبتي أوتادك» </span>
              في خطوات بسيطة وآمنة.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/services">
                <Button
                  data-testid="hero-book-session-btn"
                  className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white px-7 h-12 text-base"
                >
                  احجز جلسة الآن
                  <ArrowLeft className="w-4 h-4 me-2" />
                </Button>
              </Link>
              <Link to="/book">
                <Button
                  data-testid="hero-buy-book-btn"
                  variant="outline"
                  className="rounded-full border-[#E5E1D8] bg-white px-7 h-12 text-base"
                >
                  تصفّح الكتاب الرقمي
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 -mt-10">
        <div className="grid md:grid-cols-3 gap-6">
          <ValueCard
            icon={CalendarHeart}
            title="حجز فوري للجلسات"
            body="اختر الموعد الذي يناسبك من تقويم الأوقات المتاحة فعليًا، وأكّد حجزك بأمان."
          />
          <ValueCard
            icon={ShieldCheck}
            title="مدفوعات مُؤمّنة"
            body="تتم جميع المعاملات عبر Stripe بتشفيرٍ كامل، مع تأكيد الحجز فقط بعد إتمام الدفع."
          />
          <ValueCard
            icon={BookOpenCheck}
            title="مكتبة شخصية"
            body="من حسابك يمكنك متابعة الجلسات القادمة، السابقة، الملغاة، وتحميل كتبك الرقمية."
          />
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="order-2 lg:order-1">
          <h2 className="font-heading text-3xl sm:text-4xl gold-shimmer leading-tight">
            جلسات «أوتادك» تناسب أولوياتك
          </h2>
          <p className="mt-4 text-[#5E5C58] leading-loose">
            ثماني باقات متنوعة بين الجلسات الفردية، المكالمات والرسائل المجدولة، باقات الاشتراك الشهري،
            والجلسات الطارئة لمن تحتاج مساندة عاجلة.
          </p>
          <Link to="/services" className="inline-block mt-6">
            <Button
              data-testid="services-cta-btn"
              className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
            >
              استعراض جميع الجلسات
            </Button>
          </Link>
        </div>
        <div className="order-1 lg:order-2">
          <img
            src={SERVICES_IMG}
            alt="services"
            className="rounded-3xl border border-[#E5E1D8] w-full object-cover h-[420px]"
          />
        </div>
      </section>

      {/* BOOK PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <img
            src={BOOK_COVER}
            alt="Thabiti Awtadak book"
            className="rounded-3xl border border-[#E5E1D8] w-full object-cover h-[420px]"
          />
        </div>
        <div>
          <span className="text-[#0A4D3C] tracking-widest text-sm">كتاب رقمي</span>
          <h2 className="font-heading text-3xl sm:text-4xl gold-shimmer mt-2">
            ثبتي أوتادك
          </h2>
          <p className="mt-4 text-[#5E5C58] leading-loose">
            دليلٌ متكامل وخطة حياة عملية للسعادة، حلّ المشكلات بإبداع، علاج العلاقات، والتحكّم في
            العقل الباطن — يصلكم كملف PDF فور إتمام الدفع.
          </p>
          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-heading text-4xl text-[#0A4D3C]">٢٩٩</span>
            <span className="text-[#5E5C58]">ر.س</span>
          </div>
          <Link to="/book">
            <Button
              data-testid="book-cta-btn"
              className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white mt-6"
            >
              اقتنِ الكتاب الآن
            </Button>
          </Link>
        </div>
      </section>

      {/* PUBLIC REVIEWS — only approved entries */}
      <PublicReviews />
    </PageShell>
  );
};

export default Home;
